import { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import Message from '../../components/Message';
import {
  useGetDiscountQuery,
  useUpdateDiscountMutation,
} from '../../slices/discountApiSlice';

const DiscountManagerScreen = () => {
  const { data: discount, isLoading, error } = useGetDiscountQuery();
  const [updateDiscount, { isLoading: isUpdating }] = useUpdateDiscountMutation();

  const [isActive, setIsActive] = useState(false);
  const [percentage, setPercentage] = useState('');
  const [headline, setHeadline] = useState('');
  const [days, setDays] = useState('');
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');

  useEffect(() => {
    if (discount) {
      setIsActive(discount.isActive);
      setPercentage(discount.percentage || '');
      setHeadline(discount.headline || '');
    }
  }, [discount]);

  const handleToggle = async () => {
    const newActive = !isActive;

    if (newActive) {
      // Validate before activating
      if (!percentage || Number(percentage) <= 0 || Number(percentage) > 100) {
        toast.error('יש להזין אחוז הנחה תקין (1-100)');
        return;
      }
      if (!headline.trim()) {
        toast.error('יש להזין כותרת להנחה');
        return;
      }
      const totalSecs =
        (Number(days) || 0) * 24 * 3600 +
        (Number(hours) || 0) * 3600 +
        (Number(minutes) || 0) * 60;
      if (totalSecs <= 0) {
        toast.error('יש להזין משך זמן תקין להנחה');
        return;
      }
    }

    try {
      await updateDiscount({
        isActive: newActive,
        percentage: Number(percentage),
        headline,
        days: Number(days) || 0,
        hours: Number(hours) || 0,
        minutes: Number(minutes) || 0,
      }).unwrap();
      setIsActive(newActive);
      setDays('');
      setHours('');
      setMinutes('');
      toast.success(newActive ? 'ההנחה הופעלה בהצלחה!' : 'ההנחה בוטלה בהצלחה!');
    } catch (err) {
      toast.error(err?.data?.message || err.error);
    }
  };

  if (isLoading) return <Loader />;
  if (error) return <Message variant="danger">{error?.data?.message || error.error}</Message>;

  const isExpired =
    discount?.endTime && new Date() > new Date(discount.endTime);

  return (
    <div dir="rtl" style={{ maxWidth: '600px', margin: '0 auto' }}>
      <h1 className="text-end mb-4">מנהל הנחות</h1>

      {/* Status card */}
      <Card
        className="mb-4"
        style={{
          border: `2px solid ${isActive && !isExpired ? '#6B46C1' : '#ccc'}`,
          borderRadius: '12px',
        }}
      >
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center">
            <Button
              onClick={handleToggle}
              disabled={isUpdating}
              style={{
                backgroundColor: isActive && !isExpired ? '#e53e3e' : '#6B46C1',
                border: 'none',
                borderRadius: '25px',
                padding: '10px 30px',
                fontWeight: '600',
                fontSize: '1rem',
              }}
            >
              {isActive && !isExpired ? 'כבה הנחה' : 'הפעל הנחה'}
            </Button>
            <div className="text-end">
              <strong style={{ fontSize: '1.1rem' }}>
                סטטוס:{' '}
                <span
                  style={{
                    color: isActive && !isExpired ? '#6B46C1' : '#999',
                  }}
                >
                  {isActive && !isExpired ? 'פעיל' : 'לא פעיל'}
                </span>
              </strong>
              {isActive && discount?.endTime && (
                <div style={{ fontSize: '0.85rem', color: '#555' }}>
                  בתוקף עד:{' '}
                  {new Date(discount.endTime).toLocaleString('he-IL')}
                </div>
              )}
              {isExpired && (
                <div style={{ fontSize: '0.85rem', color: '#e53e3e' }}>
                  ההנחה פגה תוקף
                </div>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Settings form */}
      <Card style={{ borderRadius: '12px' }}>
        <Card.Body>
          <h5 className="text-end mb-3">הגדרות הנחה</h5>

          <Form.Group className="mb-3">
            <Form.Label className="d-block text-end">
              כותרת ההנחה (תוצג בבאנר)
            </Form.Label>
            <Form.Control
              type="text"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="לדוגמה: מבצע קיץ מיוחד!"
              className="text-end"
              dir="rtl"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="d-block text-end">אחוז הנחה</Form.Label>
            <div className="d-flex align-items-center gap-2 justify-content-end">
              <span>%</span>
              <Form.Control
                type="number"
                value={percentage}
                onChange={(e) => setPercentage(e.target.value)}
                min="1"
                max="100"
                style={{ maxWidth: '100px', textAlign: 'center' }}
              />
            </div>
          </Form.Group>

          <div className="mb-3">
            <Form.Label className="d-block text-end mb-2">
              משך זמן ההנחה (בעת הפעלה)
            </Form.Label>
            <Row className="g-2 flex-row-reverse">
              <Col xs={4}>
                <Form.Label className="d-block text-center" style={{ fontSize: '0.85rem' }}>
                  דקות
                </Form.Label>
                <Form.Control
                  type="number"
                  value={minutes}
                  onChange={(e) => setMinutes(e.target.value)}
                  min="0"
                  max="59"
                  placeholder="0"
                  style={{ textAlign: 'center' }}
                />
              </Col>
              <Col xs={4}>
                <Form.Label className="d-block text-center" style={{ fontSize: '0.85rem' }}>
                  שעות
                </Form.Label>
                <Form.Control
                  type="number"
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                  min="0"
                  max="23"
                  placeholder="0"
                  style={{ textAlign: 'center' }}
                />
              </Col>
              <Col xs={4}>
                <Form.Label className="d-block text-center" style={{ fontSize: '0.85rem' }}>
                  ימים
                </Form.Label>
                <Form.Control
                  type="number"
                  value={days}
                  onChange={(e) => setDays(e.target.value)}
                  min="0"
                  placeholder="0"
                  style={{ textAlign: 'center' }}
                />
              </Col>
            </Row>
          </div>

        </Card.Body>
      </Card>

      {isUpdating && <Loader />}
    </div>
  );
};

export default DiscountManagerScreen;
